/**
 * Coupon Utility Functions
 * Shared utility functions for coupon processing and calculations
 */

/**
 * Extract percentage from a coupon title
 * @param {string} title - The coupon title
 * @returns {number|null} - The extracted percentage or null
 */
export const extractPercent = (title) => {
  const match = title?.match(/(\d+)%/);
  return match ? parseInt(match[1], 10) : null;
};

/**
 * Parse price text and return price range object
 * Supports multiple formats: "$10–20", "$15", "$$$"
 * @param {string} priceText - Price text from venue data
 * @returns {object} - Object with low and high prices { low: number, high: number }
 */
export const parsePriceLevel = (priceText) => {
  if (!priceText) return { low: 20, high: 30 }; // Default to mid-range price

  // Check if it's a price range like "$10–20" or "$10-20"
  const rangeMatch = priceText.match(/\$(\d+)[–-]\$?(\d+)/);
  if (rangeMatch) {
    const low = parseInt(rangeMatch[1], 10);
    const high = parseInt(rangeMatch[2], 10);
    return { low, high };
  }

  // Check if it's a single price like "$15"
  const singlePriceMatch = priceText.match(/\$(\d+)/);
  if (singlePriceMatch) {
    const price = parseInt(singlePriceMatch[1], 10);
    return { low: price, high: price };
  }

  // Fallback to dollar sign count (e.g., "$$$" -> 3)
  const count = (priceText.match(/\$/g) || []).length;
  if (count > 0) {
    // Convert count to approximate price: $ = 15, $$ = 25, $$$ = 40, $$$$ = 60
    const priceMap = { 1: 15, 2: 25, 3: 40, 4: 60 };
    const price = priceMap[count] || 25;
    return { low: price, high: price };
  }

  return { low: 20, high: 30 }; // Default fallback
};

/**
 * Calculate dollar savings range from percentage and price range
 * @param {number} percent - The discount percentage
 * @param {object} priceRange - Object with low and high prices
 * @returns {object|null} - Object with low and high savings { low: number, high: number }
 */
export const calculateDollarSavingsRange = (percent, priceRange) => {
  if (!percent || !priceRange) return null;
  const lowSavings = Math.round((priceRange.low * percent) / 100);
  const highSavings = Math.round((priceRange.high * percent) / 100);
  return { low: lowSavings, high: highSavings };
};

/**
 * Calculate dollar savings as a formatted string
 * @param {number} percent - The discount percentage
 * @param {object} priceRange - Object with low and high prices
 * @returns {string|null} - Formatted savings string (e.g., "$3 - $6" or "$5")
 */
export const calculateDollarSavings = (percent, priceRange) => {
  const savingsRange = calculateDollarSavingsRange(percent, priceRange);
  if (!savingsRange) return null;

  if (savingsRange.low === savingsRange.high) {
    return `$${savingsRange.low}`;
  }

  return `$${savingsRange.low} - $${savingsRange.high}`;
};
